export const YMS_EXPORT_STATUS = Object.freeze({
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    SUCCESS_WITH_WARNING: 'SUCCESS_WITH_WARNING',
    ERROR: 'ERROR',
});

export const YMS_INIT_STATUS = Object.freeze({
    PENDING: 'PENDING',
    ERROR: 'ERROR',
});

export const YES_NO_UNKNOWN = Object.freeze({
    YES: 'YES',
    NO: 'NO',
    UNKNOWN: 'UNKNOWN',
});

export const FENIX_SUBMISSION_RESULT = Object.freeze({
    ERROR: 'ERROR',
    SUCCESS_WITH_WARNING: 'SUCCESS_WITH_WARNING',
    SUCCESS: 'SUCCESS',
});

export const FENIX_FLOW_STATUS = Object.freeze({
    IDLE: 'IDLE',
    IN_PROGRESS: 'IN_PROGRESS',
    ERROR: 'ERROR',
    SUCCESS: 'SUCCESS',
    SUCCESS_WITH_WARNING: 'SUCCESS_WITH_WARNING',
});

export const HISTORY_STATUS = Object.freeze({
    IDLE: 'IDLE',
    LOADING: 'LOADING',
    LOADED: 'LOADED',
    ERROR: 'ERROR',
});

export const FENIX_EXPORTED_TO_YMS_FIELD = 'exportedToYms';

export const EXPORTED_TO_YMS_SLASH_DISPLAY = '/';

export const EXPORTED_TO_YMS_DISPLAY = Object.freeze({
    [YES_NO_UNKNOWN.YES]: 'Yes',
    [YES_NO_UNKNOWN.NO]: 'No',
    [YES_NO_UNKNOWN.UNKNOWN]: EXPORTED_TO_YMS_SLASH_DISPLAY,
});

export const mapExportedToYmsDisplay = (value) => {
    if (value == null) return EXPORTED_TO_YMS_SLASH_DISPLAY;
    return EXPORTED_TO_YMS_DISPLAY[value] ?? EXPORTED_TO_YMS_SLASH_DISPLAY;
};

export const FENIX_YMS_EXPORTS_PATH = '/yms/exports';

export const buildFenixYmsHistoryPath = (transactionId) => `/yms/exports/transaction/${transactionId}`;

export const VALID_EXPORT_STATUSES = Object.freeze(new Set(Object.values(YMS_EXPORT_STATUS)));

export const isValidExportStatus = (status) => VALID_EXPORT_STATUSES.has(status);

export const resolveLookupValueFromDisplay = (display, lookup) => {
    if (display == null || !lookup?.dataSource || !lookup?.displayExpr) return display;
    const match = lookup.dataSource.find((option) => option?.[lookup.displayExpr] === display);
    return match ? match[lookup.valueExpr ?? lookup.displayExpr] : display;
};

export const sanitizeYmsUrl = (url) => {
    if (typeof url !== 'string' || !url.trim()) return null;
    try {
        const { protocol } = new URL(url);
        return protocol === 'http:' || protocol === 'https:' ? url : null;
    } catch {
        return null;
    }
};

export const mapExportStatus = (status) => {
    switch (status) {
        case YMS_EXPORT_STATUS.SUCCESS:
            return {
                flowStatus: FENIX_FLOW_STATUS.SUCCESS,
                submissionResult: FENIX_SUBMISSION_RESULT.SUCCESS,
                exportButtonLocked: true,
                suggestedExportedToYms: YES_NO_UNKNOWN.YES,
            };
        case YMS_EXPORT_STATUS.SUCCESS_WITH_WARNING:
            return {
                flowStatus: FENIX_FLOW_STATUS.SUCCESS_WITH_WARNING,
                submissionResult: FENIX_SUBMISSION_RESULT.SUCCESS_WITH_WARNING,
                exportButtonLocked: true,
                suggestedExportedToYms: YES_NO_UNKNOWN.YES,
            };
        case YMS_EXPORT_STATUS.PENDING:
            return {
                flowStatus: FENIX_FLOW_STATUS.IN_PROGRESS,
                submissionResult: null,
                exportButtonLocked: true,
                suggestedExportedToYms: null,
            };
        case YMS_EXPORT_STATUS.ERROR:
        default:
            return {
                flowStatus: FENIX_FLOW_STATUS.ERROR,
                submissionResult: FENIX_SUBMISSION_RESULT.ERROR,
                exportButtonLocked: false,
                suggestedExportedToYms: YES_NO_UNKNOWN.NO,
            };
    }
};

export const normalizeAttempt = (entry, index = 0) => {
    const status = isValidExportStatus(entry?.status) ? entry.status : YMS_EXPORT_STATUS.PENDING;
    return {
        id: entry?.requestId != null ? String(entry.requestId) : entry?.id != null ? String(entry.id) : `fenix-attempt-${index}`,
        requestId: entry?.requestId ?? null,
        status,
        message: entry?.message ?? '',
        details: entry?.details ?? null,
        url: entry?.url ?? null,
        started: entry?.started ?? null,
        finished: entry?.finished ?? null,
        transient: entry?.transient ?? false,
    };
};

const parseTime = (iso) => {
    const time = new Date(iso || 0).getTime();
    return Number.isNaN(time) ? 0 : time;
};

export const getLatestAttempt = (attempts = []) => {
    if (!attempts?.length) return null;
    let latest = null;
    let latestTime = -Infinity;
    for (let index = 0; index < attempts.length; index += 1) {
        const current = attempts[index];
        const currentTime = parseTime(current?.started);
        if (latest === null || currentTime >= latestTime) {
            latest = current;
            latestTime = currentTime;
        }
    }
    return latest;
};

export const deriveStateFromAttempts = (attempts = []) => {
    const latest = getLatestAttempt(attempts);
    if (!latest) {
        return {
            status: FENIX_FLOW_STATUS.IDLE,
            exportButtonLocked: false,
            fenixSubmissionResult: null,
            ymsUrl: null,
            ymsDetails: null,
            statusMessage: null,
            requestId: null,
        };
    }
    const mapped = mapExportStatus(latest.status);
    return {
        status: mapped.flowStatus,
        exportButtonLocked: mapped.exportButtonLocked,
        fenixSubmissionResult: mapped.submissionResult,
        ymsUrl: latest.url ?? null,
        ymsDetails: latest.details ?? null,
        statusMessage: latest.message ?? null,
        requestId: latest.requestId ?? null,
    };
};

export const FENIX_YMS_COPY = Object.freeze({
    completeFields: 'Fill in the required fields before exporting.',
    missingFieldsTemplate: 'Fill {fields} to export to YMS.',

    alreadyExported: 'This transaction has already been exported to YMS.',
    emptyTitle: 'No export activity yet.',
    emptyHint: 'Fill in the required fields on the transaction, then click Export to YMS to start the process.',
    exportButton: 'Export to YMS',
    exportButtonInProgress: 'Export in progress…',
    exportFailed: 'Error — export failed.',
    openYms: 'Open YMS',
    noYmsLink: 'No YMS link available yet',
    ymsResponseDetailsLabel: 'YMS Response Details:',
    refresh: 'Refresh',
});

export const buildProcessStartedMessage = (transactionId) =>
    `Process started for T:${transactionId}. Please wait for the process to complete before submitting or discarding this transaction.`;

export const joinWithAnd = (items = []) => {
    if (!items.length) return '';
    if (items.length === 1) return items[0];
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

export const buildMissingFieldsMessage = (missingFields = []) => {
    if (!missingFields.length) return FENIX_YMS_COPY.completeFields;
    return FENIX_YMS_COPY.missingFieldsTemplate.replace('{fields}', () => joinWithAnd(missingFields));
};

export const getExportButtonTooltip = ({ inProgress, disabled, locked, fieldsComplete, missingFields = [] }) => {
    if (inProgress || !disabled) return '';
    if (locked) return FENIX_YMS_COPY.alreadyExported;
    if (fieldsComplete) return '';
    return buildMissingFieldsMessage(missingFields);
};
